import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { cn } from '@/lib/utils'
import { examples, mediaIcons } from '../../constants/home-page'
import { Sparkles } from 'lucide-react'

// Helper function to get background color based on media type
function getIconBackgroundColor(type) {
  switch (type) {
    case 'image':
      return 'bg-pink-500'
    case 'video':
      return 'bg-purple-500'
    case '3d':
      return 'bg-blue-500'
    case 'audio':
      return 'bg-green-500'
    case 'chatgpt':
      return 'bg-amber-500'
    case 'claude':
      return 'bg-indigo-500'
    default:
      return 'bg-gray-500'
  }
}

export function PromptExamples({ setTextareaValue, setActiveTab }) {
  const [activeType, setActiveType] = useState('image')
  const [visibleExamples, setVisibleExamples] = useState([])

  const { activeTheme } = useSelector((state) => state.theme)
  const isSharpTheme = activeTheme?.includes('sharp-theme')

  // Initialize visible examples
  useEffect(() => {
    const mixedExamples = []

    // Get one random example from each category
    Object.entries(examples).forEach(([type, exampleList]) => {
      const randomIndex = Math.floor(Math.random() * exampleList.length)
      mixedExamples.push({
        type,
        text: exampleList[randomIndex],
      })
    })

    // Shuffle and take 4 examples
    const shuffled = [...mixedExamples].sort(() => 0.5 - Math.random())
    setVisibleExamples(shuffled.slice(0, 4))
  }, [])

  // Rotate through examples every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleExamples((prev) => {
        const newExamples = [...prev]

        // Replace one random example
        const indexToReplace = Math.floor(Math.random() * newExamples.length)
        const typeToReplace = newExamples[indexToReplace].type
        const exampleList = examples[typeToReplace]

        // Find examples not currently shown
        const currentTexts = newExamples.map((ex) => ex.text)
        const availableExamples = exampleList.filter((ex) => !currentTexts.includes(ex))

        // Select a new example
        if (availableExamples.length > 0) {
          const randomIndex = Math.floor(Math.random() * availableExamples.length)
          newExamples[indexToReplace] = {
            type: typeToReplace,
            text: availableExamples[randomIndex],
          }
        } else {
          const randomIndex = Math.floor(Math.random() * exampleList.length)
          newExamples[indexToReplace] = {
            type: typeToReplace,
            text: exampleList[randomIndex],
          }
        }

        return newExamples
      })
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  // Handle click on example - use the props passed from GlowingInput
  const handleExampleClick = useCallback(
    (example) => {
      // Set the textarea value using the prop function
      if (setTextareaValue) {
        setTextareaValue(example.text)
      }

      // Set the active tab using the prop function
      if (setActiveTab) {
        setActiveTab(example.type)
      }

      // Update active type for visual feedback
      setActiveType(example.type)
    },
    [setTextareaValue, setActiveTab, setActiveType]
  )

  return (
    <div className="space-y-2 xl:space-y-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex mb-8 sm:mb-4 xl:mb-2 items-center justify-center gap-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
        <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-purple-500" />
        <span className="font-medium">Try these examples:</span>
      </div>
      <div className=" grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 w-full gap-2 sm:gap-3">
        {visibleExamples.map((example, index) => {
          // Use default icon if type is not found in mediaIcons
          const IconComponent = mediaIcons[example.type] || mediaIcons.default
          const isActive = example.type === activeType

          return (
            <div
              key={index}
              onClick={() => handleExampleClick(example)}
              className={cn(
                'p-2 sm:p-3 cursor-pointer relative overflow-hidden group',
                'transition-all duration-300 ease-in-out',
                'hover:bg-white/10 hover:backdrop-blur-sm dark:hover:bg-gray-800/50 hover:scale-102',
                'border border-transparent hover:border-purple-200 dark:hover:border-purple-800',
                isSharpTheme ? 'rounded-none' : 'rounded-lg',
                isActive &&
                  'bg-white/10 backdrop-blur-sm dark:bg-gray-800/50 scale-105 border-purple-300 dark:border-purple-700'
              )}
            >
              <div
                className={cn(
                  'absolute inset-0 bg-gradient-to-r from-pink-500/10 to-purple-500/10',
                  'transition-opacity duration-300 ease-in-out',
                  'opacity-0 group-hover:opacity-100'
                )}
              ></div>
              <div className="flex items-start gap-2 relative z-10">
                <div
                  className={cn(
                    'p-1.5 sm:p-2 flex-shrink-0',
                    isSharpTheme ? 'rounded-none' : 'rounded-full',
                    getIconBackgroundColor(example.type)
                  )}
                >
                  <IconComponent className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 relative z-10',
                      isSharpTheme && 'text-white'
                    )}
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    "{example.text}"
                  </p>
                  <div
                    className={cn(
                      'h-0.5 sm:h-1 w-0 bg-gradient-to-r from-pink-500 to-purple-500 mt-1 sm:mt-2',
                      'transition-all duration-300 ease-in-out',
                      isActive && 'w-full',
                      'group-hover:w-full'
                    )}
                  ></div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
